import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// mui
import {
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
// layouts
import { Main } from "layouts/main";
// components
import { EmptyContent } from "components/empty-content";
// types
import { Subscription } from "./types/main";
// enums
import { UserType } from "enums/account";
// utils
import { getRequest, postRequest } from "utils/request";
// assets
import susbcriptionIcon from "./assets/imgs/subscription.svg";
import { ReactComponent as EditIcon } from "assets/imgs/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "assets/imgs/delete.svg";
import { ReactComponent as TeamFrame } from "./assets/imgs/team-frame.svg";
import { ReactComponent as IndividualFrame } from "./assets/imgs/individual-frame.svg";
import { ReactComponent as StarIcon } from "./assets/imgs/star.svg";
import { VisibilityOutlined } from "@mui/icons-material";
import styles from "./assets/scss/subscriptions.module.scss";
import sharedStyles from "assets/scss/_shared.module.scss";
import EditSubscription from "./edit-subscription";
import AddSubscription from "./add-subscription";
import ViewSubscription from "./view-subscription";
// redux
import { uiActions } from "store/ui/ui-slice";

const Subscriptions = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const app = useSelector((state: any) => state.app?.allData || {});

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [openCreateSubscripionDialog, setOpenCreateSubscripionDialog] =
    useState(false);
  const [subscriptionToEdit, setSubscriptionToEdit] =
    useState<Subscription | null>(null);
  const [subscriptionToView, setSubscriptionToView] =
    useState<Subscription | null>(null);

  const onToggleCreateSubscription = () => {
    setOpenCreateSubscripionDialog(!openCreateSubscripionDialog);
  };
  const onCloseEditSubscription = () => {
    setSubscriptionToEdit(null);
  };
  const handleEditSubscription = (subscription: Subscription) => {
    setSubscriptionToEdit(subscription);
  };
  const onCloseViewSubscription = () => {
    setSubscriptionToView(null);
  };
  const handleViewSubscription = (subscription: Subscription) => {
    setSubscriptionToView(subscription);
  };
  const handleDeleteSubscription = async (subscription: Subscription) => {
    const url = `delete_subscription/`;
    const response = await postRequest(
      url,
      {
        creator_member_id: app.memberId,
        entity_id: app.entityId,
        subscription_id: subscription.id,
      },
      navigate,
      {
        "Content-Type": "application/json",
      }
    );
    if (response?.data) {
      dispatch(
        uiActions.updateNotification({
          message: response?.data.message || "Failed to add subscription.",
          level: response?.data.isOk ? "success" : "error",
        })
      );
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRequest(
          `get_all_subscriptions_details/`,
          {},
          navigate
        );
        if (response?.data.data) {
          console.log(response.data.data);
          setSubscriptions(response?.data.data);
        }
      } catch (error) {
      } finally {
      }
    };

    fetchData();
  }, [openCreateSubscripionDialog, subscriptionToEdit]);
  return (
    <Main
      action={{
        title: "Add Subscription",
        clickHandler: onToggleCreateSubscription,
      }}
      header={{ icon: susbcriptionIcon, title: "Subscriptions" }}
    >
      <Typography
        variant="body2"
        sx={{
          color: theme.custom.neutralColors.color8,
          fontWeight: 600,
          fontSize: isMobile ? "18px" : "20px",
          textAlign: "left",
        }}
      >
        All Subscriptions
      </Typography>

      <hr></hr>

      {/* Projects List or Empty Content */}
      {subscriptions.length > 0 ? (
        <div className={sharedStyles.rowsContainer}>
          {subscriptions.map((subscription) => (
            <div
              key={subscription.id}
              className={sharedStyles.row}
              style={{
                borderBottom: `1px solid ${theme.custom.neutralColors.color5}`,
              }}
            >
              <div className={styles["details-container"]}>
                {subscription.account_type === UserType.INDIVIDUAL ? (
                  <IndividualFrame />
                ) : (
                  <TeamFrame />
                )}
                <div className={styles["details-sub-container"]}>
                  <span>{subscription.account_type}</span>
                  <div className={styles["tier-container"]}>
                    <StarIcon />
                    <span>{subscription.tier}</span>
                  </div>
                </div>
              </div>
              <div className={sharedStyles.actionContainer}>
                <div>
                  <IconButton
                    aria-label="edit"
                    onClick={() => handleEditSubscription(subscription)}
                    className={sharedStyles["icon-btn"]}
                  >
                    <EditIcon />
                  </IconButton>
                </div>
                <div>
                  <IconButton
                    aria-label="delete"
                    onClick={() => handleDeleteSubscription(subscription)}
                    className={sharedStyles["icon-btn"]}
                  >
                    <DeleteIcon width={26} height={26} />
                  </IconButton>
                </div>

                <div>
                  <IconButton
                    aria-label="view"
                    onClick={() => handleViewSubscription(subscription)}
                    className={sharedStyles["icon-btn"]}
                  >
                    <VisibilityOutlined />
                  </IconButton>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyContent
          text="There are no subscriptions yet. You can"
          linkText="start now."
          onChange={onToggleCreateSubscription}
        />
      )}

      {subscriptionToEdit && (
        <EditSubscription
          onClose={onCloseEditSubscription}
          subscription={subscriptionToEdit}
        />
      )}
      {openCreateSubscripionDialog && (
        <AddSubscription onClose={onToggleCreateSubscription} />
      )}
      {subscriptionToView && (
        <ViewSubscription
          onClose={onCloseViewSubscription}
          subscription={subscriptionToView}
        />
      )}
    </Main>
  );
};
export default Subscriptions;
